import { IPromise, IQService } from 'angular';
import { call, put } from 'redux-saga/effects';
import * as _ from 'underscore';
import { Messages } from '../../../../codegen/Localize';
import { RequestHelper } from '../../../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../../../httpClient/utils/ResultHelper';
import { IContentInfo } from '../../../../ts/models/IContentInfo';
import { IDataSourceInfo } from '../../../../ts/models/IDataSourceInfo';
import { IExtractTaskInfo } from '../../../../ts/models/IExtractTaskInfo';
import { IWorkbookInfo } from '../../../../ts/models/IWorkbookInfo';
import { RefreshTaskType } from '../../../../ts/ServerApi/requests/CreateSelfServiceScheduleExtractTasksRequest';
import { RemoteRefreshTypes } from '../../../../ts/ServerApi/requests/GetRemoteRefreshSchedulesRequest';
import { RunExtractRefreshesOnDatasourcesRequest } from '../../../../ts/ServerApi/requests/RunExtractRefreshesOnDatasourcesRequest';
import { RunExtractRefreshesOnWorkbooksRequest } from '../../../../ts/ServerApi/requests/RunExtractRefreshesOnWorkbooksRequest';
import { IResult } from '../../../../httpClient/serverApi/types/IResponse';
import { ToasterService, ToastType } from '../../../../ts/services/ToasterService';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { requestSpaceContents } from '../../../actions/spaces/RequestSpaceContentsActions';
import { RunExtractRefreshNowAction } from '../../../actions/spaces/RunExtractRefreshNowAction';
import { RefreshRunNowDialog, RefreshRunNowDialogProps } from '../../../content/RefreshRunNowDialog';
import { ServiceLocator } from '../../../ServiceLocator';
import { getDatasourceInfos, getWorkbookInfos } from '../SpacesSagaHelpers';

import WebHttpClient from '../../../../httpClient/WebHttpClient';

export function* handleRunExtractRefreshNowAction(action: RunExtractRefreshNowAction) {
    try {
        const itemHasIncremental = (item: any) => 'hasIncrementalExtract' in item && item.hasIncrementalExtract;
        const disableIncrementalOption = _.any(action.tasks, (item: IContentInfo) => !itemHasIncremental(item));
        let refreshType = yield call(RefreshRunNowDialog.open, action.tasks, disableIncrementalOption);
        if (refreshType) {
            // Needed because the content info may have an incorrect ID for unknown reasons
            const contentDictionaryByType = _.groupBy(action.tasks, c => c.contentType);
            const workbookInfos = contentDictionaryByType['workbook'] && (yield call(getWorkbookInfos, contentDictionaryByType['workbook']));
            const datasourceInfos = contentDictionaryByType['datasource'] && (yield call(getDatasourceInfos, contentDictionaryByType['datasource']));
            yield call(executeRunExtractRefresh, refreshType, datasourceInfos || [], workbookInfos || []);
            yield put(requestSpaceContents());
        }
    } catch (e) {
        yield put(requestFailed(e, Messages.unexpectedError()));
    }
}

export function executeDatasourceRunNow(ids: string[], extractType: string): Promise<IResult> {
    return WebHttpClient.sendRequests(
        RequestHelper.buildRequests(ids, (idsBlock) =>
            new RunExtractRefreshesOnDatasourcesRequest(idsBlock, extractType)
        ),
        ResultHelper.combineResults
    );
}

export function executeWorkbookRunNow(ids: string[], extractType: string): Promise<IResult> {
    return WebHttpClient.sendRequests(
        RequestHelper.buildRequests(ids, (idsBlock) =>
            new RunExtractRefreshesOnWorkbooksRequest(idsBlock, extractType)
        ),
        ResultHelper.combineResults);
}

export function executeRunExtractRefresh(mode: RefreshTaskType, datasources: IDataSourceInfo[], workbooks: IWorkbookInfo[]): IPromise<void> {
    const PromiseService = ServiceLocator.get<IQService>('$q');
    // we do some casts so we have a try/catch to avoid any nasty surprises
    try {
        let requestPromises = [];
        const datasourceIds = datasources.map(ds => ds.id);
        const workbookIds = workbooks.map(wb => wb.id);
        if (datasourceIds.length > 0) {
            requestPromises.push(executeDatasourceRunNow(datasourceIds, mode));
        }
        if (workbookIds.length > 0) {
            requestPromises.push(executeWorkbookRunNow(workbookIds, mode));
        }
        const combinedPromise = PromiseService.all(requestPromises);
        return combinedPromise.then(results => {
            const succesfullRequests = results.filter(result => !result.errors || result.errors.length === 0);
            if (succesfullRequests.length > 0) {
                ToasterService.getInstance().pop({
                    type: ToastType.Info,
                    text: Messages.runNowSucceeded({
                        NUM_TASKS: String(succesfullRequests.length),
                    }),
                });
            }
            const failedRequests = results.filter(result => result.errors && result.errors.length > 0);
            if (failedRequests.length > 0) {
                ToasterService.getInstance().pop({
                    type: ToastType.Error,
                    text: Messages.runNowFailed({
                        NUM_TASKS: String(failedRequests.length),
                    }),
                });
            }
        });
    } catch (exception) {
        ToasterService.getInstance().pop({
            type: ToastType.Error,
            text: Messages.runNowFailed({
                NUM_TASKS: String(datasources.length + workbooks.length),
            }),
        });
    }
    return;
}
